/**
 * Phase C · Universal Account Ledger Drill-Down.
 *
 * Route: /accounts/:kind/:accountId (e.g. /accounts/vendor/V-01)
 *
 * Shows:
 *  - Party card (name + meta)
 *  - Summary KPIs (rows, total debit/credit, net, outstanding, overdue count/amount)
 *  - Full chronological ledger with running balance
 *  - Overdue rows: red background + animate-pulse
 *  - Due-soon rows (days_to_due <= 5): amber tint
 *  - Per-row "Why this matters?" AI button on Bill / Invoice / overdue entries
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { fmtINR, fmtDate } from "../lib/format";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import WhyThisMatters from "../components/WhyThisMatters";
import { toast } from "sonner";
import {
    Building2, User, CreditCard, Users, ArrowLeft, AlertTriangle, Clock, CheckCircle2
} from "lucide-react";

const KIND_META = {
    vendor: { label: "Vendor", icon: Building2, color: "text-[var(--brand-amber)]" },
    client: { label: "Client", icon: User, color: "text-[#4af6c3]" },
    bank: { label: "Bank Account", icon: CreditCard, color: "text-[#60a5fa]" },
    employee: { label: "Employee", icon: Users, color: "text-[#c084fc]" },
};

export default function AccountLedger() {
    const { kind, accountId } = useParams();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const [r, acc] = await Promise.all([
                api.get(`/accounts/${kind}/${accountId}/ledger`, { params: { days: 730 } }),
                api.get(`/accounts/list?kind=${kind}`),
            ]);
            setData(r.data);
            setAccounts(acc.data.accounts || []);
        } catch (e) {
            toast.error("Failed to load ledger: " + (e.response?.data?.detail || e.message));
            setData(null);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, accountId]);

    const meta = KIND_META[kind] || KIND_META.vendor;
    const Icon = meta.icon;

    if (loading) return <div className="text-xs font-mono-fin text-[var(--text-secondary)] p-8">Loading ledger…</div>;
    if (!data) return (
        <div className="p-8 text-center">
            <div className="text-xs font-mono-fin uppercase tracking-wider text-[var(--neg)]">Account not found</div>
            <button onClick={() => navigate(-1)} className="mt-4 text-xs font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] hover:underline">Go back</button>
        </div>
    );

    const party = data.meta.party || {};
    const summary = data.summary || {};
    const rows = data.rows || [];

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <button onClick={() => navigate(-1)} data-testid="ledger-back" className="p-1.5 border border-panel hover:border-[var(--brand-amber)]/40 text-[var(--text-secondary)]"><ArrowLeft size={14} /></button>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)]">// {meta.label.toUpperCase()} LEDGER · DRILL-DOWN</div>
            </div>

            <Panel testId="ledger-header" title={<span className="flex items-center gap-2"><Icon className={meta.color} size={20} /> {party.name || accountId}</span>} subtitle={party.gstin || party.type || party.role || party.category || ""}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KpiCard testId="ledger-rows" label="Entries" value={summary.rows || 0} format="custom" accent />
                    <KpiCard testId="ledger-outstanding" label="Outstanding" value={fmtINR(summary.outstanding || 0)} format="custom" />
                    <KpiCard testId="ledger-overdue" label={<span className="flex items-center gap-1"><AlertTriangle size={11} className="text-[var(--neg)]" /> Overdue</span>} value={`${summary.overdue_count || 0} · ${fmtINR(summary.overdue_amount || 0)}`} format="custom" />
                    <KpiCard testId="ledger-due-soon" label={<span className="flex items-center gap-1"><Clock size={11} className="text-amber-400" /> Due in ≤5d</span>} value={`${summary.due_soon_count || 0} · ${fmtINR(summary.due_soon_amount || 0)}`} format="custom" />
                </div>

                {summary.overdue_count > 0 && (
                    <div className="mt-3">
                        <WhyThisMatters
                            kind="overdue"
                            title={`${party.name || accountId} has ${summary.overdue_count} overdue entries totalling ${fmtINR(summary.overdue_amount)}`}
                            context={{ party: party.name || accountId, kind, overdue_count: summary.overdue_count, overdue_amount: summary.overdue_amount, outstanding: summary.outstanding }}
                            label="AI · Explain the risk & recommend next steps"
                        />
                    </div>
                )}
            </Panel>

            {/* Switcher for jumping between accounts of the same kind */}
            {accounts.length > 1 && (
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Switch:</div>
                    <select
                        data-testid="ledger-switch"
                        value={accountId}
                        onChange={(e) => navigate(`/accounts/${kind}/${e.target.value}`)}
                        className="bg-[var(--bg-base)] border border-panel px-3 py-1 text-xs font-mono-fin text-[var(--text-primary)]"
                    >
                        {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} {a.meta ? `· ${a.meta}` : ""}</option>)}
                    </select>
                </div>
            )}

            <Panel testId="ledger-table-panel" title="Full Ledger" subtitle={`${rows.length} entries · newest first · running balance = Σ credits − Σ debits`}>
                {rows.length === 0 ? (
                    <div className="text-center py-8 font-mono-fin text-xs text-[var(--text-secondary)] uppercase tracking-wider">
                        No transactions recorded for this account.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full font-mono-fin text-xs" data-testid="ledger-table">
                            <thead className="border-b border-panel">
                                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-2 pr-2">Date</th>
                                    <th className="text-left pr-2">Kind</th>
                                    <th className="text-left pr-2">Ref</th>
                                    <th className="text-left pr-2">Narration</th>
                                    <th className="text-right pr-2">Debit</th>
                                    <th className="text-right pr-2">Credit</th>
                                    <th className="text-right pr-2">Balance</th>
                                    <th className="text-left pr-2">Due</th>
                                    <th className="pr-2">Insights</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => {
                                    const isOverdue = r.is_overdue;
                                    const isDueSoon = r.days_to_due != null && r.days_to_due >= 0 && r.days_to_due <= 5;
                                    let rowCls = "border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]/[0.03]";
                                    if (isOverdue) rowCls += " bg-[var(--neg)]/10 animate-pulse";
                                    else if (isDueSoon) rowCls += " bg-amber-500/10";

                                    return (
                                        <tr key={i} className={rowCls} data-testid={`ledger-row-${i}`}>
                                            <td className="py-1.5 pr-2 whitespace-nowrap text-[var(--text-primary)]/85">{fmtDate(r.date)}</td>
                                            <td className="pr-2 text-[var(--brand-amber)] whitespace-nowrap">{r.kind}</td>
                                            <td className="pr-2 text-[var(--text-primary)]/70 max-w-[140px] truncate" title={r.ref}>{r.ref || "—"}</td>
                                            <td className="pr-2 text-[var(--text-primary)] max-w-md truncate" title={r.narration}>{r.narration}</td>
                                            <td className="pr-2 text-right text-neg">{r.debit ? fmtINR(r.debit) : "—"}</td>
                                            <td className="pr-2 text-right text-pos">{r.credit ? fmtINR(r.credit) : "—"}</td>
                                            <td className={`pr-2 text-right ${r.running_balance < 0 ? "text-neg" : "text-[var(--text-primary)]/85"}`}>{fmtINR(r.running_balance)}</td>
                                            <td className="pr-2 whitespace-nowrap">
                                                {r.due_date ? (
                                                    <span className={`text-[10px] ${isOverdue ? "text-neg font-bold" : isDueSoon ? "text-amber-400 font-bold" : "text-[var(--text-primary)]/70"}`}>
                                                        {fmtDate(r.due_date)}
                                                        {r.days_to_due != null && (
                                                            <div className="text-[9px] uppercase tracking-wider">{isOverdue ? `${Math.abs(r.days_to_due)}d overdue` : `${r.days_to_due}d left`}</div>
                                                        )}
                                                    </span>
                                                ) : (
                                                    <span className="text-[var(--text-muted)]">—</span>
                                                )}
                                            </td>
                                            <td className="pr-2">
                                                {(isOverdue || isDueSoon || (r.debit || 0) > 500000) && (
                                                    <WhyThisMatters
                                                        kind={isOverdue ? "overdue" : (r.debit || 0) > 500000 ? "large_txn" : "cashflow"}
                                                        title={`${r.kind} · ${r.ref} · ${fmtINR(r.debit || r.credit)} · ${r.narration.slice(0, 60)}`}
                                                        context={{
                                                            party: party.name || accountId,
                                                            kind: r.kind,
                                                            invoice_no: r.ref,
                                                            amount: r.debit || r.credit,
                                                            outstanding: r.outstanding || 0,
                                                            days_to_due: r.days_to_due,
                                                            due_date: r.due_date,
                                                            date: r.date,
                                                            narration: r.narration,
                                                            days_overdue: r.days_to_due != null && r.days_to_due < 0 ? Math.abs(r.days_to_due) : 0,
                                                        }}
                                                        compact
                                                        label="AI"
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>
        </div>
    );
}
